import { GenerateRequest, PhotoData, StyleTemplate } from '../types/template';

class BackendService {
  private eventSource: EventSource | null = null;
  private sessionId: string | null = null;
  public backendUrl: string;
  private backendToken: string | null = null;

  constructor() {
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    this.backendToken = process.env.REACT_APP_BACKEND_TOKEN || null;
  }

  // 生成唯一的会话ID
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 连接SSE接口
  connect(templateId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // 生成新的会话ID
        this.sessionId = this.generateSessionId();
        
        // 构建SSE URL
        const sseUrl = `${this.backendUrl}/api/photo-transfer/user-photo/${this.sessionId}`;
        
        // 创建EventSource连接
        this.eventSource = new EventSource(sseUrl);
        
        // 连接成功
        this.eventSource.onopen = () => {
          resolve(this.sessionId!);
        };

        // 连接错误
        this.eventSource.onerror = (error) => {
          console.error('SSE连接错误:', error);
          reject(new Error('SSE连接失败'));
        };
        
      } catch (error) {
        console.error('创建SSE连接失败:', error);
        reject(error);
      }
    });
  }

  // 监听照片上传事件
  onPhotoReceived(callback: (photoData: PhotoData) => void) {
    if (this.eventSource) {
      this.eventSource.addEventListener('photo', (event) => {
        try {
          const data = JSON.parse(event.data) as PhotoData;
          // console.log('收到照片数据:', {
          //   sessionId: data.sessionId,
          //   filename: data.filename,
          //   mimetype: data.mimetype,
          //   size: data.size,
          //   timestamp: data.timestamp,
          //   imageDataLength: data.imageData?.length || 0
          // });
          
          if (data.imageData && data.sessionId) {
            callback(data);
          }
        } catch (error) {
          console.error('解析照片数据失败:', error);
        }
      });
    }
  }

  // 监听连接状态变化
  onConnectionChange(callback: (status: 'connected' | 'connecting' | 'disconnected' | 'error') => void) {
    if (this.eventSource) {
      this.eventSource.onopen = () => {
        callback('connected');
      };
      
      this.eventSource.onerror = () => {
        callback('error');
      };
    }
  }

  // 获取当前会话ID
  getSessionId(): string | null {
    return this.sessionId;
  }

  // 断开连接
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.sessionId = null;
    console.log('SSE连接已断开');
  }

  // 检查连接状态
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  // 将base64转换为Blob
  private base64ToBlob(base64: string): Blob {
    // 移除base64前缀 (data:image/jpeg;base64,)
    const base64Data = base64.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'image/jpeg' });
  }

  // 上传照片到会话
  async uploadPhotoToSession(sessionId: string, photoData: string): Promise<void> {
    // 将base64转换为Blob
    const blob = this.base64ToBlob(photoData);
    
    // 创建FormData并添加文件
    const formData = new FormData();
    formData.append('photo', blob, 'photo.jpg');

    const response = await fetch(`${this.backendUrl}/api/photo-transfer/upload/${sessionId}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo to session');
    }
  }

  // 上传生成的图片到后端服务器
  async uploadToBackend(imageData: ArrayBuffer | Blob, filename: string) {
    try {
      const form = new FormData();
      
      // 确保数据是Blob格式
      const blob = imageData instanceof Blob ? imageData : new Blob([imageData], { type: 'image/jpeg' });
      form.append('files', blob, filename);

      const response = await fetch(`${this.backendUrl}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`
        },
        body: form
      });

      if (!response.ok) {
        throw new Error('Failed to upload file to backend');
      }

      const data = await response.json();
      
      // 新接口返回数组格式，取第一条结果的url
      if (Array.isArray(data) && data.length > 0) {
        return {
          url: `${this.backendUrl}${data[0].url}`,
          data: data[0] // 保留完整的第一条数据供后续使用
        };
      } else {
        throw new Error('Upload response format error: expected array with at least one item');
      }
    } catch (error) {
      console.error('Upload to backend failed:', error);
      throw error;
    }
  }

  // 获取风格列表
  async getStyleList(query: { pagination: { page: number; pageSize: number } }): Promise<{
    items: StyleTemplate[];
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  }> {
    try {
      const { pagination } = query;
      const searchParams = new URLSearchParams({
        'pagination[page]': pagination.page.toString(),
        'pagination[pageSize]': pagination.pageSize.toString(),
        'fields': 'id, name',
        'populate[image][fields][0]': 'url'
      });

      const response = await fetch(`${this.backendUrl}/api/painting-workflows?${searchParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch style list');
      }

      const data = await response.json();
      
      // 转换API返回的数据格式为StyleTemplate格式
      const items: StyleTemplate[] = data.data.map((item: any) => {
        let imageUrl = item.image?.url || '';
        
        // 如果是相对路径，则加上 backendUrl
        if (imageUrl && !imageUrl.startsWith('http')) {
          imageUrl = `${this.backendUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
        }
        
        return {
          id: item.documentId,
          name: item.name,
          imageUrl,
          categoryId: 'all' // 默认分类，可根据实际API调整
        };
      });

      return {
        items,
        pagination: data.meta.pagination
      };
    } catch (error) {
      console.error('获取风格列表失败:', error);
      throw error;
    }
  }

  // 获取前端配置信息
  async getConfig(device: string, internal: string = '0'): Promise<any> {
    try {
      const response = await fetch(`${this.backendUrl}/api/painting-config/frontend/${device}?internal=${internal}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch config');
      }

      const responseData = await response.json();
      return responseData.data;
    } catch (error) {
      console.error('获取配置信息失败:', error);
      throw error;
    }
  }

  
  /**
   * 上传图片到云创
   * @param file 文件数据
   * @returns {object} 上传结果 { success: boolean, url: string, internalUrl: string }
   */
  async uploadImageToYunchuang(file: string) {
    try {
      // 只需传递base64Image字段
      const response = await fetch(`${this.backendUrl}/api/yunchuang/upload-base64-image-with-auth`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          base64Image: file
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to upload base64 image: ${response.status} ${response.statusText}. ${errorData.message || ''}`);
      }

      const result = await response.json();
      return {
        success: true,
        url: result.url,
        internalUrl: result.internalUrl,
        ...result
      };
    } catch (error) {
      console.error('上传到云创失败:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 创建风格化图片生成任务
   * @param {GenerateRequest} request 生成请求参数
   * @returns 生成响应
   */
  async stylizeWorkflowCreateTask(request: GenerateRequest): Promise<{ success: boolean; data?: { taskId: string } }> {
    try {
      const response = await fetch(`${this.backendUrl}/api/yunchuang/create-styletransfer-workflow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workflowId: request.styleId,
          sourceImage: request.sourceImage
        }),
      });

      if (!response.ok) {
        throw new Error(`Generate failed: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`获取任务状态失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  /**
   * 获取风格化图片生成任务状态
   * @param taskId 任务ID
   * @returns { success: boolean; data?: { state: number; errorMessage?: string; imageUrl?: string } } 任务状态， state: 0-等待执行；1-执行中；2-执行失败；3-执行成功
   */
  async stylizeWorkflowGetTaskStatus(taskId: string): Promise<{ success: boolean; data?: { state: number; errorMessage?: string; imageUrl?: string } }> {
    try {
      const response = await fetch(`${this.backendUrl}/api/yunchuang/styletransfer-workflow/${taskId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Get task status failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`获取任务状态失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  /**
   * 获取用户会员类型
   * @param phone 用户手机号
   * @returns 用户会员信息
   */
  async getUserMemberType(phone: string): Promise<{
    success: boolean;
    data?: Array<{
        memberType: number;
        memberId: string;
        memberName: string;
        freeFlow: string;
        startTime: string;
        endTime: string;
        subProductIds: string[];
      }>;
    usageCount?: number;
  }> {
    try {
      const response = await fetch(`${this.backendUrl}/api/yunchuang/woapp/get-user-member-type`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        throw new Error(`Get user member type failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`获取用户会员类型失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  /**
   * 记录会员使用日志
   * @param phone 用户手机号
   * @param itemName 项目名称
   * @returns 记录结果
   */
  async paintingMemrberLog(phone: string, itemName: string): Promise<{
    success: boolean;
    message?: string;
  }> {
    try {
      const response = await fetch(`${this.backendUrl}/api/painting-member-logs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.backendToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { mobile: phone, itemName } }),
      });

      if (!response.ok) {
        throw new Error(`Painting member log failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`记录会员日志失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }
}

export const backendService = new BackendService();
